import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CollaboratorSettingsProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface Collaborator {
  id: string;
  userId: string | null;
  email: string;
  displayName: string | null;
  permissions: {
    view: boolean;
    edit: boolean;
    delete: boolean;
    manage: boolean;
    results: boolean;
    approve: boolean;
  };
  approvalStatus: 'pending' | 'approved' | 'rejected' | 'not_required';
  status: 'pending' | 'active' | 'removed';
  createdAt: string;
  acceptedAt: string | null;
}

interface NewCollaborator {
  email: string;
  permissions: {
    view: boolean;
    edit: boolean;
    full: boolean;
    results: boolean;
    approve: boolean;
  };
}

export default function CollaboratorSettings({ formId, isOpen, onClose }: CollaboratorSettingsProps) {
  const { t } = useTranslation('builder');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // New collaborator form
  const [newEmail, setNewEmail] = useState('');
  const [newPermissions, setNewPermissions] = useState<NewCollaborator['permissions']>({
    view: true,
    edit: false,
    full: false,
    results: false,
    approve: false,
  });

  useEffect(() => {
    if (isOpen && formId) {
      loadCollaborators();
    }
  }, [isOpen, formId]);

  async function loadCollaborators() {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/forms/${formId}/collaborators`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load collaborators');
      }

      const result = await response.json() as { collaborators: Collaborator[] };
      setCollaborators(result.collaborators.filter(c => c.status !== 'removed'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function addCollaborator() {
    if (!newEmail.trim()) {
      setError(t('collaboration.errors.emailRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(`/api/forms/${formId}/collaborators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          collaborators: [{
            email: newEmail.trim(),
            permissions: newPermissions,
          }],
        }),
      });

      const result = await response.json() as {
        success?: boolean;
        error?: string;
        added?: Array<{ email: string; status: string }>;
        errors?: Array<{ email: string; error: string }>;
      };

      if (!response.ok) {
        throw new Error(result.error || 'Failed to add collaborator');
      }

      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0].error);
        return;
      }

      setSuccessMessage(t('collaboration.successAdded', { email: newEmail }));
      setNewEmail('');
      setNewPermissions({
        view: true,
        edit: false,
        full: false,
        results: false,
        approve: false,
      });
      await loadCollaborators();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('collaboration.errors.addFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function updatePermissions(collaboratorId: string, permissions: Collaborator['permissions']) {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/forms/${formId}/collaborators/${collaboratorId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permissions }),
      });

      if (!response.ok) {
        const result = await response.json() as { error?: string };
        throw new Error(result.error || 'Failed to update permissions');
      }

      await loadCollaborators();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('collaboration.errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function removeCollaborator(collaboratorId: string, email: string) {
    if (!confirm(t('collaboration.confirmRemove', { email }))) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/forms/${formId}/collaborators/${collaboratorId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const result = await response.json() as { error?: string };
        throw new Error(result.error || 'Failed to remove collaborator');
      }

      setSuccessMessage(t('collaboration.successRemoved', { email }));
      await loadCollaborators();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('collaboration.errors.removeFailed'));
    } finally {
      setSaving(false);
    }
  }

  function getStatusBadge(status: Collaborator['status']) {
    switch (status) {
      case 'active':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">{t('collaboration.status.active')}</span>;
      case 'pending':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">{t('collaboration.status.pending')}</span>;
      default:
        return null;
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('collaboration.title')}</h2>
              <p className="text-sm text-gray-500">{t('collaboration.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {successMessage}
            </div>
          )}

          {/* Add Collaborator Form */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('collaboration.addCollaborator')}</h3>
            <div className="flex gap-3 mb-3">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('collaboration.emailPlaceholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={addCollaborator}
                disabled={saving || !newEmail.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? t('collaboration.addingButton') : t('collaboration.addButton')}
              </button>
            </div>

            {/* Permissions */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newPermissions.view}
                  onChange={(e) => setNewPermissions(p => ({ ...p, view: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{t('collaboration.permissions.view')}</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newPermissions.edit}
                  onChange={(e) => setNewPermissions(p => ({ ...p, edit: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{t('collaboration.permissions.edit')}</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newPermissions.results}
                  onChange={(e) => setNewPermissions(p => ({ ...p, results: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{t('collaboration.permissions.viewResults')}</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newPermissions.approve}
                  onChange={(e) => setNewPermissions(p => ({ ...p, approve: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{t('collaboration.permissions.canApprove')}</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newPermissions.full}
                  onChange={(e) => setNewPermissions(p => ({ ...p, full: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="font-medium">{t('collaboration.permissions.fullAccess')}</span>
              </label>
            </div>
          </div>

          {/* Collaborators List */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              {t('collaboration.currentCollaborators', { count: collaborators.length })}
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : collaborators.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p>{t('collaboration.noCollaborators')}</p>
                <p className="text-sm">{t('collaboration.noCollaboratorsDescription')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {collaborators.map((collab) => (
                  <div
                    key={collab.id}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {(collab.displayName || collab.email)[0].toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {collab.displayName || collab.email}
                          </span>
                          {getStatusBadge(collab.status)}
                        </div>
                        {collab.displayName && (
                          <span className="text-sm text-gray-500">{collab.email}</span>
                        )}
                        <div className="flex gap-2 mt-1">
                          {collab.permissions.view && (
                            <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">{t('collaboration.permissions.view')}</span>
                          )}
                          {collab.permissions.edit && (
                            <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded">{t('collaboration.permissions.edit')}</span>
                          )}
                          {collab.permissions.results && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-600 rounded">{t('collaboration.permissions.viewResults')}</span>
                          )}
                          {collab.permissions.approve && (
                            <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-600 rounded">{t('collaboration.permissions.canApprove')}</span>
                          )}
                          {collab.permissions.manage && (
                            <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-600 rounded">{t('collaboration.permissions.fullAccess')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const newPerms = { ...collab.permissions };
                          newPerms.edit = !newPerms.edit;
                          updatePermissions(collab.id, newPerms);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title={t('collaboration.tooltips.toggleEdit')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => removeCollaborator(collab.id, collab.email)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('collaboration.tooltips.remove')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            {t('collaboration.closeButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
